import createModule from "../createModule"
import { get, pick } from "lodash"
import { getTopicId, getModels } from "modules/research-topic-root/selectors"
import { getResearchTopicDate } from "modules/research-topic-root/selectors/dates"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as SecuritiesFinderCreators } from "modules/securities-finder/model"
import { Creators as SecuritiesFinderFacetsCreators } from "modules/securities-finder-facets/model"
import { Creators as SmartTradeRulesSearchCreators } from "modules/smart-trade-rule-search/model"
import { Creators as notificationCreators } from "modules/notifications/model"
import { getContentSets } from "selectors/contentSets"
import { getPositionSet } from "helpers/comparison"
import { classificationsQuery } from "helpers/classification"
import { getUnmodeledDates } from "selectors/dates"
import view from "./view"
import { Creators } from "./model"
import { getTradeSize, getSecuritiesCount, getInstrumentSecurityFilter, getChipsInfo, getSelectedComparisonType, getBaseUrl, getDisplayTradeSize, getBasketRules, getComparisonTypes, getOptimizationStatus, getExperiment, getSmartTradePositionSet, getScorecardHeadline, getSmartTradeRules, getCalculatedRefEquity, getEditedExperiment, getSegmentNormalization, getComparisonItems, getSmartTradeRefEquities, getSmartTradeErrors } from "./selectors"
import { getModelLatestDate, getSelectedDate, getEndDate, getIsSingleDate, getInstrumentLatestDate, getInstrumentEarliestDate, getLatestDate, getStartDate, getScorecardDate, getPeriod } from "./selectors/dates"
import { getModelId } from "./selectors/model"
import { getInstrument, getInstrumentPortfolio, getInstrumentDescriptors, getInstrumentPortfolioPositions, getInstrumentPortfolioGMV, getInstrumentLongOrShort, getModelHasDates } from "./selectors/instrument"
import { getSteps, getActiveStep, getUrlStep, getCompletedStep } from "./selectors/steps"
import { getFacetedFilter } from "modules/securities-finder/selectors/filter"
import { getClassifications } from "modules/smart-trade-rule-search/selectors"
import { ASSETS_SUBQUERY } from "modules/research-topic/helpers"
import withOutletContext from "hocs/withOutletContext"
import withUrlParams from "hocs/withUrlParams"
import withNavigate from "hocs/withNavigate"
import { MODEL_METADATA_QUERY } from "resources/models"
import "./style.scss"

export default withOutletContext(withUrlParams(withNavigate(createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    researchTopicId: getTopicId(props),
    referenceInstrument: getInstrument(state, props),
    modelId: getModelId(state, props),
    models: getModels(state, props),
    modelLatestDate: getModelLatestDate(state, props),
    activeStep: getActiveStep(state, props),
    filter: getFacetedFilter(state, props),
    positionSets: getComparisonItems(state, props),
    scorecardDate: getScorecardDate(state, props),
    startDate: getStartDate(state, props),
    endDate: getEndDate(state, props),
    editedExperiment: getEditedExperiment(state, props),
    classifications: getClassifications(state, props),
    instrumentEarliestDate: getInstrumentEarliestDate(state, props),
    instrumentLatestDate: getInstrumentLatestDate(state, props),
    tradeDate: getResearchTopicDate(state, props),
  }),
  required: ["account", "accountId", "researchTopicId", "models", "classifications"],
  data: [{
    resource: "smartTradeInstrumentDescriptors",
    url: ({ referenceInstrument, modelId, modelLatestDate }) => {
      if (!modelId || !referenceInstrument.securityId || !modelLatestDate) {
        return null
      }

      return {
        id: `instrumentDescriptors-${JSON.stringify(referenceInstrument.securityId)}-${modelId}-${modelLatestDate}`,
        query: `query ($modelId: String!, $ticker: String, $mic: String, $date: Date!, $internalId: String) {
          model(id:$modelId) {
            security(ticker: $ticker, mic: $mic, internalId: $internalId){
              descriptors(on: $date){
                ticker
                name
                mic
                assetClass
                modelProviderId
                internalId
              }
              availability {
                from
                to
              }
            }
          }
        }`,
        variables: {
          date: modelLatestDate,
          modelId,
          ...pick(referenceInstrument.securityId, ["ticker", "mic", "internalId"]),
        },
      }
    },
  },
  {
    resource: "instrumentPortfolio",
    url: ({ referenceInstrument }) => {
      if (!referenceInstrument.portfolioId) { return null }
      return ({
        id: `instrument-${referenceInstrument.portfolioId}`,
        query: `query ($portfolioId: String!) {
          portfolio(id: $portfolioId) {
            id
            name
            description
            alias
            availableFrom
            fxHedgeEnabled
          }
        }`,
        variables: {
          portfolioId: referenceInstrument.portfolioId,
        },
      })
    },
  },
  {
    resource: "instrumentPortfolioPositions",
    passthrough: true,
    url: ({ modelId, referenceInstrument, tradeDate, endDate }) => {
      if (!modelId || (!tradeDate && !endDate) || !referenceInstrument.portfolioId) { return null }
      const date = tradeDate || endDate
      return ({
        id: `instrument-${modelId}-${date}-${referenceInstrument.portfolioId}`,
        query: `query ($portfolioId: String!, $modelId: String!, $to: Date!, $from: Date!) {
          portfolio(id: $portfolioId) {
            dates(to: $to, from: $from, modelId: $modelId) {
              gmv
              equity
              ${ASSETS_SUBQUERY}
            }
          }
        }`,
        variables: {
          modelId,
          portfolioId: referenceInstrument.portfolioId,
          from: date,
          to: date,
        },
      })
    },
  },
  {
    resource: "instrumentPortfolioModelMeta",
    url: ({ account, referenceInstrument, modelId }) => {
      if (!modelId || !referenceInstrument.portfolioId) { return null }
      return `/api/${account}/m/${modelId}/r/portfolio/${referenceInstrument.portfolioId}`
    },
  },
  {
    resource: "smartTradeRisk",
    passthrough: true,
    url: ({ modelId, positionSets, scorecardDate }) => {
      if (!modelId || !positionSets || !scorecardDate) {
        return null
      }

      const positionSet = getPositionSet(positionSets, 0, scorecardDate)
      const smartTradePositionSet = getPositionSet(positionSets, 2, scorecardDate)

      if (!positionSet || !smartTradePositionSet) {
        return null
      }

      return {
        id: `${modelId}${JSON.stringify(positionSet)}${JSON.stringify(smartTradePositionSet)}${scorecardDate}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $smartTradePositionSet: PositionSetInput! $from: Date!, $to: Date!) {
          model (id: $modelId) {
            refInstrument: simulation (positionSet: $positionSet, from: $from, to: $to, interval: AUTO) {
              risk {
                attribution {
                  summary {
                    specific
                  }
                }
              }
            },
            smartTrade: simulation (positionSet: $smartTradePositionSet, from: $from, to: $to, interval: AUTO) {
              risk {
                attribution {
                  summary {
                    specific
                  }
                }
              }
            },
          }
        }`,
        variables: {
          modelId,
          positionSet,
          smartTradePositionSet,
          from: scorecardDate,
          to: scorecardDate,
        },
      }
    },
  },
  {
    resource: "smartTradeRefEquities",
    passthrough: true,
    url: ({ modelId, positionSets, startDate, endDate }) => {
      if (!modelId || !positionSets || !startDate || !endDate) {
        return null
      }

      const smartTradePositionSet = getPositionSet(positionSets, 2, startDate)
      if (!smartTradePositionSet) {
        return null
      }

      return {
        id: `smartTradeRefEquities-${modelId}${JSON.stringify(smartTradePositionSet)}${startDate}${endDate}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!,$from: Date!, $to: Date!) {
          model (id: $modelId) {
            simulation (positionSet: $positionSet, from: $from, to: $to, interval: AUTO) {
              composition (scaleFormat: PERCENT_EQUITY_GMV) {
                date
                gmv
              }
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: smartTradePositionSet,
          from: startDate,
          to: endDate,
        },
      }
    },
  }, {
    resource: "smartTradeExperimentPositions",
    url: ({ editedExperiment, researchTopicId }) => {
      if (!editedExperiment || !researchTopicId) {
        return null
      }

      const tradeDate = get(editedExperiment, "config.setup.tradeDate")

      return {
        id: `smartTradeExperimentPositions-${researchTopicId}${editedExperiment.id}${tradeDate}`,
        query: `query ($researchTopicId: String!, $tradeDate: Date!, $experimentId: String!) {
          researchTopic (id: $researchTopicId) {
            experiment (id: $experimentId) {
              dates (from: $tradeDate, to: $tradeDate) {
                date
                equity
                equities {
                  id {
                    ticker
                    mic
                    modelProviderId
                    internalId
                  }
                  economicExposure
                }
                swaps {
                  id
                  economicExposure
                }
                currencies {
                  id,
                  economicExposure
                }
                fixedIncome {
                  id {
                    isin
                  }
                  economicExposure
                }
                commodities {
                  id
                  economicExposure
                }
                indexes {
                  id
                  economicExposure
                }
                otherAssets {
                  id
                  economicExposure
                }
              }
            }
          }
        }`,
        variables: {
          researchTopicId,
          experimentId: editedExperiment.id,
          tradeDate,
        },
      }
    },
  }, {
    resource: "classifications",
    as: null,
    url: () => ({
      id: "classifications",
      query: classificationsQuery,
    }),
  }, {
    resource: "modeledDates",
    as: null,
    url: ({ modelId, instrumentLatestDate, instrumentEarliestDate }) => {
      if (!modelId || !instrumentLatestDate || !instrumentEarliestDate) {
        return null
      }

      return {
        id: `${modelId}${instrumentLatestDate}${instrumentEarliestDate}`,
        query: "query ($modelId: String!, $from: Date!, $to: Date!) { model (id: $modelId) { availability { dates(from: $from, to: $to) } } }",
        variables: { modelId, from: instrumentEarliestDate, to: instrumentLatestDate },
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    steps: getSteps(props),
    step: getUrlStep(props),
    instrumentPortfolio: getInstrumentPortfolio(state, props),
    instrumentPortfolioPositions: getInstrumentPortfolioPositions(state, props),
    instrumentPortfolioGMV: getInstrumentPortfolioGMV(state, props),
    instrumentDescriptors: getInstrumentDescriptors(state, props),
    instrumentEarliestDate: getInstrumentEarliestDate(state, props),
    instrumentLatestDate: getInstrumentLatestDate(state, props),
    securitiesCount: getSecuritiesCount(state, props),
    instrumentSecurityFilter: getInstrumentSecurityFilter(state, props),
    chipsInfo: getChipsInfo(state, props),
    comparisonItems: getComparisonItems(state, props),
    comparisonTypes: getComparisonTypes(state, props),
    selectedComparisonType: getSelectedComparisonType(state, props),
    endDate: getEndDate(state, props),
    tradeDate: getResearchTopicDate(state, props),
    period: getPeriod(state, props),
    latestDate: getLatestDate(state, props),
    startDate: getStartDate(state, props),
    baseUrl: getBaseUrl(state, props),
    scorecardDate: getScorecardDate(state, props),
    displayTradeSize: getDisplayTradeSize(state, props),
    contentSets: getContentSets(state, props),
    selectedDate: getSelectedDate(state, props),
    tradeSize: getTradeSize(state, props),
    basketRules: getBasketRules(state, props),
    smartTradeRules: getSmartTradeRules(state, props),
    status: getOptimizationStatus(state, props),
    isSingleDate: getIsSingleDate(state, props),
    experiment: getExperiment(state, props),
    scorecardHeadline: getScorecardHeadline(state, props),
    smartTradePositionSet: getSmartTradePositionSet(state, props),
    completedStep: getCompletedStep(state, props),
    calculatedRefEquity: getCalculatedRefEquity(state, props),
    segmentNormalization: getSegmentNormalization(state, props),
    unmodeledDates: getUnmodeledDates(state, props),
    smartTradeRefEquities: getSmartTradeRefEquities(state, props),
    longOrShortInstrument: getInstrumentLongOrShort(state, props),
    modelHasDates: getModelHasDates(state, props),
    errors: getSmartTradeErrors(state, props),
  }),
  actions: {
    ...Creators,
    openDialog: DialogCreators.open,
    resetSecurityFinderFilters: SecuritiesFinderCreators.resetFilters,
    loadSecurityFinderFilters: SecuritiesFinderCreators.loadFilters,
    resetSecurityFinderFacets: SecuritiesFinderFacetsCreators.reset,
    resetSmartTradesRulesSearch: SmartTradeRulesSearchCreators.resetSmartTradeRules,
    addNotification: notificationCreators.add,

  },
}))))
